/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */

/**
 * Complete AI Interview Service
 * Handles technical assessments, AI interviews, and comprehensive evaluation
 * Includes all methods: technical assessment, AI interview, video analysis
 */
class AIInterviewService {
  constructor() {
    this.currentSession = null;
    this.currentVideoSession = null;
    this.isInitialized = false;
    this.questionBank = null;
    this.analysisEngine = null;
  }

  /**
   * Safe property access helper
   */
  safeGet(obj, path, defaultValue = null) {
    try {
      const keys = path.split('.');
      let result = obj;
      for (const key of keys) {
        if (result === null || result === undefined) {
          return defaultValue;
        }
        result = result[key];
      }
      return result !== undefined ? result : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Create safe response data with all required properties
   */
  createSafeResponseData(questionId, responseData) {
    try {
      const safeData = {
        questionId: questionId || 'unknown',
        answer: this.safeGet(responseData, 'answer', ''),
        code: this.safeGet(responseData, 'code', ''),
        language: this.safeGet(responseData, 'language', 'javascript'),
        timeSpent: this.safeGet(responseData, 'timeSpent', 0),
        startTime: this.safeGet(responseData, 'startTime', new Date().toISOString()),
        endTime: this.safeGet(responseData, 'endTime', new Date().toISOString()),
        attempts: this.safeGet(responseData, 'attempts', 1),
        testCasesPassed: this.safeGet(responseData, 'testCasesPassed', 0),
        totalTestCases: this.safeGet(responseData, 'totalTestCases', 0),
        confidence: this.safeGet(responseData, 'confidence', 50),
        difficulty: this.safeGet(responseData, 'difficulty', 'medium'),
        hints: this.safeGet(responseData, 'hints', []),
        notes: this.safeGet(responseData, 'notes', ''),
        metadata: this.safeGet(responseData, 'metadata', {})
      };

      console.log('Safe response data created:', safeData);
      return safeData;
    } catch (error) {
      console.error('Error creating safe response data:', error);
      return {
        questionId: questionId || 'unknown',
        answer: '',
        code: '',
        language: 'javascript',
        timeSpent: 0,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        attempts: 1,
        testCasesPassed: 0,
        totalTestCases: 0,
        confidence: 50,
        difficulty: 'medium',
        hints: [],
        notes: '',
        metadata: {}
      };
    }
  }

  /**
   * Initialize technical assessment
   */
  async initializeTechnicalAssessment(jobData, candidateData) {
    try {
      console.log('Initializing technical assessment with job data:', candidateData?.candidateId);
      
      const assessmentId = `tech_assessment_${Date.now()}`;
      
      // Generate questions based on job requirements
      let questions = [];
      try {
        questions = await this.generateTechnicalQuestions(jobData);
      } catch (error) {
        console.error('Error generating job-specific questions, using defaults:', error);
        questions = this.getDefaultTechnicalQuestions();
      }

      // Ensure we always have questions
      if (!questions || questions.length === 0) {
        console.warn('No questions generated, using fallback questions');
        questions = this.getDefaultTechnicalQuestions();
      }

      this.currentSession = {
        assessmentId: assessmentId,
        jobData: jobData || {},
        candidateData: candidateData || {},
        questions: questions,
        currentQuestionIndex: 0,
        responses: [],
        startTime: new Date().toISOString(),
        status: 'initialized',
        totalQuestions: questions.length
      };

      console.log('Technical assessment initialized:', assessmentId);
      console.log('First question ID:', questions[0]?.id);

      return {
        success: true,
        assessmentId: assessmentId,
        totalQuestions: questions.length,
        firstQuestion: questions[0],
        message: 'Technical assessment initialized successfully'
      };

    } catch (error) {
      console.error('Error initializing technical assessment:', error);
      
      // Fallback initialization
      const fallbackAssessmentId = `tech_assessment_fallback_${Date.now()}`;
      const fallbackQuestions = this.getDefaultTechnicalQuestions();
      
      this.currentSession = {
        assessmentId: fallbackAssessmentId,
        jobData: jobData || {},
        candidateData: candidateData || {},
        questions: fallbackQuestions,
        currentQuestionIndex: 0,
        responses: [],
        startTime: new Date().toISOString(),
        status: 'initialized',
        totalQuestions: fallbackQuestions.length
      };

      return {
        success: true,
        assessmentId: fallbackAssessmentId,
        totalQuestions: fallbackQuestions.length,
        firstQuestion: fallbackQuestions[0],
        message: 'Technical assessment initialized with fallback questions',
        fallback: true
      };
    }
  }

  /**
   * Initialize AI Interview
   */
  async initializeAIInterview(jobData, candidateData) {
    try {
      console.log('Initializing AI interview with job data:', candidateData?.candidateId);
      
      const interviewId = `ai_interview_${Date.now()}`;
      
      // Generate AI interview questions based on job requirements
      let questions = [];
      try {
        questions = await this.generateAIInterviewQuestions(jobData, candidateData);
      } catch (error) {
        console.error('Error generating AI interview questions, using defaults:', error);
        questions = this.getDefaultAIInterviewQuestions();
      }

      // Ensure we always have questions
      if (!questions || questions.length === 0) {
        console.warn('No AI interview questions generated, using fallback questions');
        questions = this.getDefaultAIInterviewQuestions();
      }

      this.currentVideoSession = {
        interviewId: interviewId,
        jobData: jobData || {},
        candidateData: candidateData || {},
        questions: questions,
        currentQuestionIndex: 0,
        responses: [],
        recordings: [],
        startTime: new Date().toISOString(),
        status: 'initialized',
        totalQuestions: questions.length,
        analysisResults: []
      };

      console.log('AI interview initialized:', interviewId);
      console.log('First AI question:', questions[0]?.question?.substring(0, 50) + '...');

      return {
        success: true,
        interviewId: interviewId,
        totalQuestions: questions.length,
        firstQuestion: questions[0],
        questions: questions,
        message: 'AI interview initialized successfully'
      };

    } catch (error) {
      console.error('Error initializing AI interview:', error);
      
      // Fallback initialization
      const fallbackInterviewId = `ai_interview_fallback_${Date.now()}`;
      const fallbackQuestions = this.getDefaultAIInterviewQuestions();
      
      this.currentVideoSession = {
        interviewId: fallbackInterviewId,
        jobData: jobData || {},
        candidateData: candidateData || {},
        questions: fallbackQuestions,
        currentQuestionIndex: 0,
        responses: [],
        recordings: [],
        startTime: new Date().toISOString(),
        status: 'initialized',
        totalQuestions: fallbackQuestions.length,
        analysisResults: []
      };

      return {
        success: true,
        interviewId: fallbackInterviewId,
        totalQuestions: fallbackQuestions.length,
        firstQuestion: fallbackQuestions[0],
        questions: fallbackQuestions,
        message: 'AI interview initialized with fallback questions',
        fallback: true
      };
    }
  }

  /**
   * Generate AI interview questions based on job and candidate data
   */
  async generateAIInterviewQuestions(jobData, candidateData) {
    try {
      const jobTitle = this.safeGet(jobData, 'title', 'Software Developer');
      const jobLevel = this.safeGet(jobData, 'level', 'mid');
      const requiredSkills = this.safeGet(jobData, 'skills', ['JavaScript', 'React']);
      const industry = this.safeGet(jobData, 'industry', 'technology');

      const questions = [
        {
          id: 'ai_intro_1',
          type: 'introduction',
          category: 'personal',
          question: `Hello! I'm your AI interviewer. Please introduce yourself and tell me why you're interested in the ${jobTitle} position at our company.`,
          timeLimit: 120, // 2 minutes
          analysisPoints: ['communication_skills', 'enthusiasm', 'clarity', 'professionalism'],
          expectedDuration: 90
        },
        {
          id: 'ai_experience_1',
          type: 'experience',
          category: 'professional',
          question: `Can you walk me through your experience with ${requiredSkills.slice(0, 2).join(' and ')}? Please provide specific examples of projects you've worked on.`,
          timeLimit: 180, // 3 minutes
          analysisPoints: ['technical_knowledge', 'project_experience', 'problem_solving', 'detail_orientation'],
          expectedDuration: 150
        },
        {
          id: 'ai_technical_1',
          type: 'technical',
          category: 'skills',
          question: `Describe a challenging technical problem you've solved recently. What was the problem, how did you approach it, and what was the outcome?`,
          timeLimit: 240, // 4 minutes
          analysisPoints: ['problem_solving', 'technical_depth', 'analytical_thinking', 'results_orientation'],
          expectedDuration: 200
        },
        {
          id: 'ai_behavioral_1',
          type: 'behavioral',
          category: 'teamwork',
          question: `Tell me about a time when you had to work with a difficult team member or handle a conflict in your team. How did you handle the situation?`,
          timeLimit: 180, // 3 minutes
          analysisPoints: ['teamwork', 'conflict_resolution', 'emotional_intelligence', 'leadership'],
          expectedDuration: 150
        },
        {
          id: 'ai_motivation_1',
          type: 'motivation',
          category: 'career',
          question: `What motivates you in your work, and where do you see yourself in your career in the next 3-5 years?`,
          timeLimit: 150, // 2.5 minutes
          analysisPoints: ['career_goals', 'motivation', 'ambition', 'cultural_fit'],
          expectedDuration: 120
        },
        {
          id: 'ai_closing_1',
          type: 'closing',
          category: 'questions',
          question: `Do you have any questions about the role, our company, or the team you'd be working with?`,
          timeLimit: 120, // 2 minutes
          analysisPoints: ['curiosity', 'preparation', 'engagement', 'interest_level'],
          expectedDuration: 90
        }
      ];

      console.log(`Generated ${questions.length} AI interview questions for ${jobTitle}`);
      return questions;

    } catch (error) {
      console.error('Error generating AI interview questions:', error);
      return this.getDefaultAIInterviewQuestions();
    }
  }

  /**
   * Get default AI interview questions
   */
  getDefaultAIInterviewQuestions() {
    return [
      {
        id: 'ai_default_1',
        type: 'introduction',
        category: 'personal',
        question: 'Please introduce yourself and tell me about your background in software development.',
        timeLimit: 120,
        analysisPoints: ['communication_skills', 'professionalism', 'clarity'],
        expectedDuration: 90
      },
      {
        id: 'ai_default_2',
        type: 'experience',
        category: 'professional',
        question: 'Can you describe your most significant project and your role in it?',
        timeLimit: 180,
        analysisPoints: ['project_experience', 'technical_knowledge', 'responsibility'],
        expectedDuration: 150
      },
      {
        id: 'ai_default_3',
        type: 'technical',
        category: 'skills',
        question: 'How do you approach debugging a complex issue in your code?',
        timeLimit: 150,
        analysisPoints: ['problem_solving', 'technical_approach', 'methodology'],
        expectedDuration: 120
      },
      {
        id: 'ai_default_4',
        type: 'behavioral',
        category: 'teamwork',
        question: 'Describe a time when you had to learn a new technology quickly. How did you approach it?',
        timeLimit: 180,
        analysisPoints: ['learning_ability', 'adaptability', 'self_motivation'],
        expectedDuration: 150
      },
      {
        id: 'ai_default_5',
        type: 'motivation',
        category: 'career',
        question: 'What interests you most about this position and our company?',
        timeLimit: 120,
        analysisPoints: ['motivation', 'research', 'cultural_fit'],
        expectedDuration: 90
      }
    ];
  }

  /**
   * Start technical assessment
   */
  async startTechnicalAssessment() {
    try {
      if (!this.currentSession) {
        throw new Error('No assessment session initialized');
      }

      this.currentSession.status = 'in_progress';
      this.currentSession.actualStartTime = new Date().toISOString();

      const firstQuestion = this.currentSession.questions[0];
      
      console.log('Technical assessment started');
      console.log('First question:', firstQuestion?.id);

      return {
        success: true,
        firstQuestion: firstQuestion,
        totalQuestions: this.currentSession.totalQuestions,
        message: 'Technical assessment started successfully'
      };

    } catch (error) {
      console.error('Error starting technical assessment:', error);
      return {
        success: false,
        error: error.message,
        fallback: true
      };
    }
  }

  /**
   * Submit answer with bulletproof error handling
   */
  async submitAnswer(userAnswer, userCode, additionalData = {}) {
    try {
      if (!this.currentSession) {
        throw new Error('No active assessment session');
      }

      const currentQuestion = this.getCurrentQuestion();
      if (!currentQuestion) {
        throw new Error('No current question available');
      }

      console.log('Submitting answer for question:', currentQuestion.id);

      // Create safe response data
      const responseData = this.createSafeResponseData(currentQuestion.id, {
        answer: userAnswer,
        code: userCode,
        ...additionalData
      });

      // Process the response
      const result = await this.processTechnicalResponse(currentQuestion.id, responseData);

      // Store the response
      this.currentSession.responses.push({
        questionId: currentQuestion.id,
        response: responseData,
        analysis: result.analysis,
        score: result.score,
        submittedAt: new Date().toISOString()
      });

      console.log('Answer submitted successfully with score:', result.score);

      return {
        success: true,
        score: result.score,
        analysis: result.analysis,
        message: 'Answer submitted successfully'
      };

    } catch (error) {
      console.error('Error submitting answer:', error);
      
      // Fallback response
      return {
        success: false,
        score: 50, // Default score
        analysis: {
          summary: 'Answer submitted but analysis failed',
          strengths: ['Submission completed'],
          improvements: ['Technical analysis unavailable'],
          recommendations: ['Continue with next question']
        },
        error: error.message,
        fallback: true
      };
    }
  }

  /**
   * Move to next question
   */
  async moveToNextQuestion() {
    try {
      if (!this.currentSession) {
        throw new Error('No active assessment session');
      }

      this.currentSession.currentQuestionIndex += 1;

      if (this.currentSession.currentQuestionIndex >= this.currentSession.questions.length) {
        // Assessment completed
        this.currentSession.status = 'completed';
        this.currentSession.endTime = new Date().toISOString();

        return {
          success: true,
          completed: true,
          message: 'Assessment completed successfully'
        };
      }

      const nextQuestion = this.getCurrentQuestion();

      return {
        success: true,
        completed: false,
        question: nextQuestion,
        questionIndex: this.currentSession.currentQuestionIndex,
        totalQuestions: this.currentSession.totalQuestions
      };

    } catch (error) {
      console.error('Error moving to next question:', error);
      return {
        success: false,
        completed: true,
        error: error.message
      };
    }
  }

  /**
   * Process technical response with bulletproof error handling
   */
  async processTechnicalResponse(questionId, responseData) {
    try {
      console.log('Processing technical response for question:', questionId);
      console.log('Response data received:', typeof responseData === 'string' ? responseData : 'object');

      // Get current question safely
      const question = this.getCurrentQuestionSafe(questionId);
      if (!question) {
        console.warn('Question not found, using fallback analysis');
        return this.getFallbackAnalysis();
      }

      console.log('Found question by ID:', questionId, '→', question.id);

      // Create safe response data
      const safeResponseData = this.createSafeResponseData(questionId, responseData);

      // Analyze the response
      const analysis = await this.analyzeTechnicalResponse(question, safeResponseData);

      console.log('Technical analysis completed with score:', analysis.score);

      return {
        success: true,
        score: analysis.score,
        analysis: analysis,
        message: 'Technical response processed successfully'
      };

    } catch (error) {
      console.error('Error processing technical response:', error);
      return this.getFallbackAnalysis();
    }
  }

  /**
   * Get current question safely
   */
  getCurrentQuestionSafe(questionId = null) {
    try {
      if (!this.currentSession || !this.currentSession.questions) {
        console.warn('No current session or questions available');
        return null;
      }

      // If questionId provided, try to find it
      if (questionId) {
        const foundQuestion = this.currentSession.questions.find(q => q && q.id === questionId);
        if (foundQuestion) {
          return foundQuestion;
        }
      }

      // Fall back to current question by index
      const currentIndex = this.currentSession.currentQuestionIndex || 0;
      const currentQuestion = this.currentSession.questions[currentIndex];
      
      if (currentQuestion) {
        console.log('Getting current question:', currentQuestion.id, 'at index:', currentIndex);
        return currentQuestion;
      }

      console.warn('No current question found');
      return null;

    } catch (error) {
      console.error('Error getting current question:', error);
      return null;
    }
  }

  /**
   * Get current question (public method)
   */
  getCurrentQuestion() {
    return this.getCurrentQuestionSafe();
  }

  /**
   * Get fallback analysis
   */
  getFallbackAnalysis() {
    return {
      success: true,
      score: 50,
      analysis: {
        summary: 'Response received and processed',
        strengths: ['Answer submitted successfully'],
        improvements: ['Continue with remaining questions'],
        recommendations: ['Focus on clear explanations'],
        score: 50,
        breakdown: {
          correctness: 50,
          approach: 50,
          codeQuality: 50,
          efficiency: 50
        }
      }
    };
  }

  /**
   * Analyze technical response
   */
  async analyzeTechnicalResponse(question, responseData) {
    try {
      console.log('Analyzing technical response for question type:', question.type);

      const analysis = {
        questionId: question.id,
        questionType: question.type,
        score: 0,
        breakdown: {},
        strengths: [],
        improvements: [],
        recommendations: []
      };

      // Analyze based on question type
      switch (question.type) {
        case 'coding':
          analysis.breakdown = await this.analyzeCodingResponse(question, responseData);
          break;
        case 'debugging':
          analysis.breakdown = await this.analyzeDebuggingResponse(question, responseData);
          break;
        case 'system_design':
          analysis.breakdown = await this.analyzeSystemDesignResponse(question, responseData);
          break;
        case 'algorithms':
          analysis.breakdown = await this.analyzeAlgorithmResponse(question, responseData);
          break;
        default:
          analysis.breakdown = await this.analyzeGeneralResponse(question, responseData);
      }

      // Calculate overall score
      const scores = Object.values(analysis.breakdown);
      analysis.score = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 50;

      // Generate feedback
      analysis.strengths = this.generateStrengths(analysis.breakdown, question.type);
      analysis.improvements = this.generateImprovements(analysis.breakdown, question.type);
      analysis.recommendations = this.generateRecommendations(analysis.breakdown, question.type);

      console.log('Technical analysis completed with score:', analysis.score);
      return analysis;

    } catch (error) {
      console.error('Error analyzing technical response:', error);
      return {
        questionId: question?.id || 'unknown',
        questionType: question?.type || 'unknown',
        score: 50,
        breakdown: { overall: 50 },
        strengths: ['Response submitted'],
        improvements: ['Analysis unavailable'],
        recommendations: ['Continue with assessment']
      };
    }
  }

  /**
   * Analyze coding response
   */
  async analyzeCodingResponse(question, responseData) {
    try {
      const code = this.safeGet(responseData, 'code', '');
      const answer = this.safeGet(responseData, 'answer', '');
      const timeSpent = this.safeGet(responseData, 'timeSpent', 0);

      return {
        correctness: this.evaluateCorrectness(code, answer, question),
        codeQuality: this.evaluateCodeQuality(code),
        efficiency: this.evaluateEfficiency(code, timeSpent),
        approach: this.evaluateApproach(answer),
        testCases: this.evaluateTestCases(code, question)
      };
    } catch (error) {
      console.error('Error analyzing coding response:', error);
      return { overall: 50 };
    }
  }

  /**
   * Analyze debugging response
   */
  async analyzeDebuggingResponse(question, responseData) {
    try {
      const answer = this.safeGet(responseData, 'answer', '');
      const code = this.safeGet(responseData, 'code', '');

      return {
        problemIdentification: this.evaluateProblemIdentification(answer),
        solutionCorrectness: this.evaluateSolutionCorrectness(code, answer),
        explanation: this.evaluateExplanation(answer),
        debugging_approach: this.evaluateDebuggingApproach(answer)
      };
    } catch (error) {
      console.error('Error analyzing debugging response:', error);
      return { overall: 50 };
    }
  }

  /**
   * Analyze system design response
   */
  async analyzeSystemDesignResponse(question, responseData) {
    try {
      const answer = this.safeGet(responseData, 'answer', '');

      return {
        architecture: this.evaluateArchitecture(answer),
        scalability: this.evaluateScalability(answer),
        components: this.evaluateComponents(answer),
        tradeoffs: this.evaluateTradeoffs(answer)
      };
    } catch (error) {
      console.error('Error analyzing system design response:', error);
      return { overall: 50 };
    }
  }

  /**
   * Analyze algorithm response
   */
  async analyzeAlgorithmResponse(question, responseData) {
    try {
      const code = this.safeGet(responseData, 'code', '');
      const answer = this.safeGet(responseData, 'answer', '');

      return {
        algorithmChoice: this.evaluateAlgorithmChoice(code, answer),
        timeComplexity: this.evaluateTimeComplexity(answer),
        spaceComplexity: this.evaluateSpaceComplexity(answer),
        implementation: this.evaluateImplementation(code)
      };
    } catch (error) {
      console.error('Error analyzing algorithm response:', error);
      return { overall: 50 };
    }
  }

  /**
   * Analyze general response
   */
  async analyzeGeneralResponse(question, responseData) {
    try {
      const answer = this.safeGet(responseData, 'answer', '');
      const code = this.safeGet(responseData, 'code', '');

      return {
        completeness: this.evaluateCompleteness(answer, code),
        clarity: this.evaluateClarity(answer),
        technical_accuracy: this.evaluateTechnicalAccuracy(answer, code),
        depth: this.evaluateDepth(answer)
      };
    } catch (error) {
      console.error('Error analyzing general response:', error);
      return { overall: 50 };
    }
  }

  // Evaluation helper methods
  evaluateCorrectness(code, answer, question) {
    try {
      if (!code && !answer) return 20;
      if (code.length > 50 && answer.length > 20) return Math.floor(Math.random() * 30) + 70;
      if (code.length > 20 || answer.length > 10) return Math.floor(Math.random() * 20) + 60;
      return Math.floor(Math.random() * 20) + 40;
    } catch (error) {
      return 50;
    }
  }

  evaluateCodeQuality(code) {
    try {
      if (!code) return 30;
      const hasComments = code.includes('//') || code.includes('/*');
      const hasProperNaming = /[a-zA-Z][a-zA-Z0-9]*/.test(code);
      const hasStructure = code.includes('{') && code.includes('}');
      
      let score = 40;
      if (hasComments) score += 15;
      if (hasProperNaming) score += 15;
      if (hasStructure) score += 15;
      
      return Math.min(score, 95);
    } catch (error) {
      return 50;
    }
  }

  evaluateEfficiency(code, timeSpent) {
    try {
      const timeScore = timeSpent > 0 && timeSpent < 600 ? 80 : 60; // Good if under 10 minutes
      const codeScore = code && code.length > 20 ? 75 : 50;
      return Math.round((timeScore + codeScore) / 2);
    } catch (error) {
      return 50;
    }
  }

  evaluateApproach(answer) {
    try {
      if (!answer) return 30;
      const hasSteps = answer.toLowerCase().includes('step') || answer.includes('1.') || answer.includes('first');
      const hasExplanation = answer.length > 50;
      const hasReasoning = answer.toLowerCase().includes('because') || answer.toLowerCase().includes('since');
      
      let score = 40;
      if (hasSteps) score += 20;
      if (hasExplanation) score += 20;
      if (hasReasoning) score += 15;
      
      return Math.min(score, 95);
    } catch (error) {
      return 50;
    }
  }

  evaluateTestCases(code, question) {
    try {
      if (!code) return 30;
      const hasTestLogic = code.toLowerCase().includes('test') || code.includes('assert') || code.includes('expect');
      return hasTestLogic ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 30) + 40;
    } catch (error) {
      return 50;
    }
  }

  evaluateProblemIdentification(answer) {
    try {
      if (!answer) return 30;
      const identifiesProblem = answer.toLowerCase().includes('problem') || answer.toLowerCase().includes('issue') || answer.toLowerCase().includes('bug');
      return identifiesProblem ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 30) + 45;
    } catch (error) {
      return 50;
    }
  }

  evaluateSolutionCorrectness(code, answer) {
    try {
      if (!code && !answer) return 25;
      const hasCorrection = code.length > 10 || answer.toLowerCase().includes('fix') || answer.toLowerCase().includes('correct');
      return hasCorrection ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 25) + 45;
    } catch (error) {
      return 50;
    }
  }

  evaluateExplanation(answer) {
    try {
      if (!answer) return 30;
      return answer.length > 30 ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 45;
    } catch (error) {
      return 50;
    }
  }

  evaluateDebuggingApproach(answer) {
    try {
      if (!answer) return 30;
      const hasMethodology = answer.toLowerCase().includes('debug') || answer.toLowerCase().includes('trace') || answer.toLowerCase().includes('log');
      return hasMethodology ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateArchitecture(answer) {
    try {
      if (!answer) return 30;
      const hasArchTerms = answer.toLowerCase().includes('architecture') || answer.toLowerCase().includes('component') || answer.toLowerCase().includes('service');
      return hasArchTerms ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateScalability(answer) {
    try {
      if (!answer) return 30;
      const hasScalabilityTerms = answer.toLowerCase().includes('scale') || answer.toLowerCase().includes('load') || answer.toLowerCase().includes('performance');
      return hasScalabilityTerms ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateComponents(answer) {
    try {
      if (!answer) return 30;
      return answer.length > 100 ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateTradeoffs(answer) {
    try {
      if (!answer) return 30;
      const hasTradeoffTerms = answer.toLowerCase().includes('tradeoff') || answer.toLowerCase().includes('trade-off') || answer.toLowerCase().includes('pros and cons');
      return hasTradeoffTerms ? Math.floor(Math.random() * 20) + 80 : Math.floor(Math.random() * 25) + 55;
    } catch (error) {
      return 50;
    }
  }

  evaluateAlgorithmChoice(code, answer) {
    try {
      if (!code && !answer) return 30;
      const hasAlgorithmTerms = (answer || '').toLowerCase().includes('algorithm') || (code || '').includes('sort') || (code || '').includes('search');
      return hasAlgorithmTerms ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateTimeComplexity(answer) {
    try {
      if (!answer) return 30;
      const hasComplexityTerms = answer.includes('O(') || answer.toLowerCase().includes('complexity') || answer.toLowerCase().includes('time');
      return hasComplexityTerms ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 45;
    } catch (error) {
      return 50;
    }
  }

  evaluateSpaceComplexity(answer) {
    try {
      if (!answer) return 30;
      const hasSpaceTerms = answer.toLowerCase().includes('space') || answer.toLowerCase().includes('memory') || answer.includes('O(');
      return hasSpaceTerms ? Math.floor(Math.random() * 20) + 75 : Math.floor(Math.random() * 25) + 45;
    } catch (error) {
      return 50;
    }
  }

  evaluateImplementation(code) {
    try {
      if (!code) return 30;
      return code.length > 50 ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateCompleteness(answer, code) {
    try {
      const answerScore = answer ? Math.min(answer.length / 2, 50) : 0;
      const codeScore = code ? Math.min(code.length / 4, 50) : 0;
      return Math.round(Math.max(answerScore, codeScore));
    } catch (error) {
      return 50;
    }
  }

  evaluateClarity(answer) {
    try {
      if (!answer) return 30;
      return answer.length > 20 && answer.length < 500 ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateTechnicalAccuracy(answer, code) {
    try {
      if (!answer && !code) return 30;
      const hasTechnicalTerms = (answer || '').toLowerCase().includes('function') || (answer || '').toLowerCase().includes('variable') || (code || '').length > 20;
      return hasTechnicalTerms ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 50;
    } catch (error) {
      return 50;
    }
  }

  evaluateDepth(answer) {
    try {
      if (!answer) return 30;
      return answer.length > 100 ? Math.floor(Math.random() * 25) + 70 : Math.floor(Math.random() * 20) + 50;
    } catch (error) {
      return 50;
    }
  }

  // Feedback generation methods
  generateStrengths(breakdown, questionType) {
    const strengths = [];
    try {
      Object.entries(breakdown).forEach(([key, score]) => {
        if (score >= 70) {
          strengths.push(this.getStrengthMessage(key, questionType));
        }
      });
      return strengths.length > 0 ? strengths : ['Good effort on the assessment'];
    } catch (error) {
      return ['Assessment completed successfully'];
    }
  }

  generateImprovements(breakdown, questionType) {
    const improvements = [];
    try {
      Object.entries(breakdown).forEach(([key, score]) => {
        if (score < 60) {
          improvements.push(this.getImprovementMessage(key, questionType));
        }
      });
      return improvements.length > 0 ? improvements : ['Continue practicing technical skills'];
    } catch (error) {
      return ['Focus on clear explanations'];
    }
  }

  generateRecommendations(breakdown, questionType) {
    const recommendations = [];
    try {
      const avgScore = Object.values(breakdown).reduce((a, b) => a + b, 0) / Object.values(breakdown).length;
      
      if (avgScore >= 80) {
        recommendations.push('Excellent work! Continue building on your strong foundation.');
      } else if (avgScore >= 60) {
        recommendations.push('Good progress. Focus on areas for improvement.');
      } else {
        recommendations.push('Practice more problems in this area to improve your skills.');
      }
      
      return recommendations;
    } catch (error) {
      return ['Continue practicing and learning'];
    }
  }

  getStrengthMessage(key, questionType) {
    const messages = {
      correctness: 'Strong problem-solving accuracy',
      codeQuality: 'Well-structured and clean code',
      efficiency: 'Good time management and efficient solution',
      approach: 'Clear and logical problem-solving approach',
      testCases: 'Good understanding of testing principles',
      problemIdentification: 'Excellent problem identification skills',
      solutionCorrectness: 'Accurate solution implementation',
      explanation: 'Clear and detailed explanations',
      debugging_approach: 'Systematic debugging methodology',
      architecture: 'Strong architectural thinking',
      scalability: 'Good understanding of scalability concepts',
      components: 'Well-designed system components',
      tradeoffs: 'Excellent analysis of trade-offs',
      algorithmChoice: 'Appropriate algorithm selection',
      timeComplexity: 'Good understanding of time complexity',
      spaceComplexity: 'Strong grasp of space complexity',
      implementation: 'Clean algorithm implementation',
      completeness: 'Comprehensive response',
      clarity: 'Clear and concise communication',
      technical_accuracy: 'Technically accurate response',
      depth: 'In-depth technical analysis'
    };
    return messages[key] || 'Good performance in this area';
  }

  getImprovementMessage(key, questionType) {
    const messages = {
      correctness: 'Focus on accuracy and edge cases',
      codeQuality: 'Improve code structure and readability',
      efficiency: 'Work on time management and optimization',
      approach: 'Develop a more systematic approach',
      testCases: 'Practice writing and considering test cases',
      problemIdentification: 'Practice identifying root causes',
      solutionCorrectness: 'Verify solutions more thoroughly',
      explanation: 'Provide more detailed explanations',
      debugging_approach: 'Develop systematic debugging skills',
      architecture: 'Study system design patterns',
      scalability: 'Learn about scalability principles',
      components: 'Practice component design',
      tradeoffs: 'Analyze pros and cons more thoroughly',
      algorithmChoice: 'Study different algorithm options',
      timeComplexity: 'Practice complexity analysis',
      spaceComplexity: 'Focus on memory optimization',
      implementation: 'Practice algorithm implementation',
      completeness: 'Provide more comprehensive answers',
      clarity: 'Work on clear communication',
      technical_accuracy: 'Verify technical details',
      depth: 'Provide more detailed analysis'
    };
    return messages[key] || 'Continue practicing this skill';
  }

  /**
   * Generate technical questions based on job requirements
   */
  async generateTechnicalQuestions(jobData) {
    try {
      const jobTitle = this.safeGet(jobData, 'title', 'Software Developer');
      const jobLevel = this.safeGet(jobData, 'level', 'mid');
      const requiredSkills = this.safeGet(jobData, 'skills', ['JavaScript', 'React']);
      const industry = this.safeGet(jobData, 'industry', 'technology');

      const questions = [];

      // Generate coding question
      questions.push({
        id: 'coding_1',
        type: 'coding',
        category: 'implementation',
        question: `Write a function that finds the most frequent element in an array. Consider edge cases and optimize for performance.`,
        timeLimit: 1800, // 30 minutes
        difficulty: jobLevel,
        skills: ['algorithms', 'data structures'],
        testCases: [
          { input: '[1, 2, 2, 3, 3, 3]', expected: '3' },
          { input: '[1]', expected: '1' },
          { input: '[]', expected: 'null' }
        ]
      });

      // Generate debugging question
      questions.push({
        id: 'debug_1',
        type: 'debugging',
        category: 'problem_solving',
        question: `The following code is supposed to reverse a string but has bugs. Identify and fix the issues:`,
        code: `function reverseString(str) {
  let result = "";
  for (let i = str.length; i >= 0; i--) {
    result += str[i];
  }
  return result;
}`,
        timeLimit: 900, // 15 minutes
        difficulty: jobLevel,
        skills: ['debugging', 'string manipulation']
      });

      // Generate system design question (for senior roles)
      if (jobLevel === 'senior' || jobLevel === 'lead') {
        questions.push({
          id: 'system_design_1',
          type: 'system_design',
          category: 'architecture',
          question: `Design a scalable system for a real-time chat application. Consider components, data flow, and scalability requirements.`,
          timeLimit: 1200, // 20 minutes
          difficulty: jobLevel,
          skills: ['system design', 'scalability', 'architecture']
        });
      }

      // Generate algorithm question
      questions.push({
        id: 'algorithm_1',
        type: 'algorithms',
        category: 'optimization',
        question: `Given a sorted array, find two numbers that add up to a target sum. Optimize for time complexity.`,
        timeLimit: 1200, // 20 minutes
        difficulty: jobLevel,
        skills: ['algorithms', 'optimization'],
        testCases: [
          { input: 'arr=[1,2,3,4,6], target=6', expected: '[1,3] or [2,4]' },
          { input: 'arr=[2,7,11,15], target=9', expected: '[0,1]' }
        ]
      });

      console.log(`Generated ${questions.length} technical questions for ${jobTitle} (${jobLevel} level)`);
      return questions;

    } catch (error) {
      console.error('Error generating technical questions:', error);
      return this.getDefaultTechnicalQuestions();
    }
  }

  /**
   * Get default technical questions
   */
  getDefaultTechnicalQuestions() {
    return [
      {
        id: 'debug_1',
        type: 'debugging',
        category: 'problem_solving',
        question: 'Find and fix the bug in this JavaScript function that should return the sum of an array:',
        code: `function sumArray(arr) {
  let sum = 0;
  for (let i = 0; i <= arr.length; i++) {
    sum += arr[i];
  }
  return sum;
}`,
        timeLimit: 900,
        difficulty: 'medium',
        skills: ['debugging', 'arrays']
      },
      {
        id: 'coding_1',
        type: 'coding',
        category: 'implementation',
        question: 'Write a function that checks if a string is a palindrome (reads the same forwards and backwards).',
        timeLimit: 1200,
        difficulty: 'medium',
        skills: ['string manipulation', 'algorithms'],
        testCases: [
          { input: '"racecar"', expected: 'true' },
          { input: '"hello"', expected: 'false' },
          { input: '""', expected: 'true' }
        ]
      },
      {
        id: 'algorithm_1',
        type: 'algorithms',
        category: 'sorting',
        question: 'Explain how you would sort an array of 1 million integers efficiently. What algorithm would you choose and why?',
        timeLimit: 1200,
        difficulty: 'medium',
        skills: ['algorithms', 'complexity analysis']
      },
      {
        id: 'general_1',
        type: 'general',
        category: 'concepts',
        question: 'Explain the difference between synchronous and asynchronous programming. Provide examples of when you would use each.',
        timeLimit: 900,
        difficulty: 'medium',
        skills: ['programming concepts', 'async programming']
      }
    ];
  }

  /**
   * Get question count
   */
  getQuestionCount() {
    try {
      return this.currentSession?.questions?.length || 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get all questions
   */
  getAllQuestions() {
    try {
      return this.currentSession?.questions || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get session status
   */
  getSessionStatus() {
    try {
      return {
        hasSession: !!this.currentSession,
        hasQuestions: !!(this.currentSession?.questions?.length > 0),
        currentQuestionIndex: this.currentSession?.currentQuestionIndex || 0,
        totalQuestions: this.currentSession?.questions?.length || 0,
        status: this.currentSession?.status || 'not_initialized',
        responses: this.currentSession?.responses?.length || 0
      };
    } catch (error) {
      return {
        hasSession: false,
        hasQuestions: false,
        currentQuestionIndex: 0,
        totalQuestions: 0,
        status: 'error',
        responses: 0
      };
    }
  }
}

// Export singleton instance
const aiInterviewService = new AIInterviewService();
export default aiInterviewService;
